import { AnnotationEditorType } from "pdfjs-dist";
import type { PDFViewer } from "pdfjs-dist/web/pdf_viewer.mjs";
import { stempelMasse } from "./platzieren";

// Die Maßrechnung liegt bei den reinen Platzier-Helfern (ohne PDF.js-Import, damit die
// Vorschau sie im jsdom nutzen kann) — hier weiter erreichbar.
export { stempelMasse };

/**
 * Bild (Unterschrift, Stempel) als PDF.js-Stempel-Editor an eine beliebige Stelle setzen.
 *
 * WEG: `layer.pasteEditor({ mode: STAMP }, { bitmapFile, x, y })` — derselbe Pfad, den PDF.js
 * beim Einfügen eines Bildes aus der Zwischenablage nimmt. Das Ergebnis ist ein normaler
 * Stempel-Editor: verschieben, an den Ecken skalieren, Entf löscht, Strg+Z nimmt zurück, und
 * `pdf.saveDocument()` schreibt ihn als `/Subtype /Stamp`-Annotation mit Bild (`/AP`).
 *
 * Aus dem pdf.js-Quelltext (pdfjs-dist 5.4.296, `build/pdf.mjs`):
 * - `pasteEditor` spreizt `...params` NACH dem `x/y` des Seitenmittelpunkts → eigene `x/y`
 *   gewinnen. Sie sind CSS-Pixel in der UNGEDREHTEN Editor-Ebene (so wie `event.offsetX`
 *   beim Klick); `isCentered: true` bleibt gesetzt, d. h. der Punkt wird zur Bildmitte.
 * - Größe: `StampEditor.#createCanvas` nimmt `this.width/height` (Anteile der Seite), wenn sie
 *   schon gesetzt sind — sonst die Pixelgröße des Bildes als pt. Die Bitmap lädt asynchron
 *   (`imageManager.getFromFile` → `createImageBitmap`), darum setzen wir `width/height` direkt
 *   nach `pasteEditor`, bevor das Bild da ist. Damit das auch beim zweiten Einsetzen desselben
 *   Bildes gilt, bekommt jede Datei einen eigenen Namen (der Bild-Cache schlüsselt nach
 *   Name/Größe/Datum und antwortet sonst fast sofort).
 * - Die Editor-Ebene dreht sich bei `pagesRotation` per CSS (`data-main-rotation`); der
 *   Klickpunkt muss deshalb erst in ungedrehte Ebenen-Koordinaten (`bildschirmZuEbene`).
 * - Im STAMP-Modus erzeugt ein Klick auf die leere Seite KEINEN neuen leeren Stempel
 *   (`AnnotationEditorLayer.pointerup` → nur `unselectAll`), der Modus darf also bleiben.
 *
 * SPIKE 28.09.2026 (headless Chromium, PDF.js-Viewer mit A4-PDF, Maßstab „page-width“):
 * - `stempelSetzen(viewer, png600×200, { seitenIndex: 0, cssX: 200, cssY: 300 }, 150)` →
 *   Stempelmitte bei (199,96 | 299,83) relativ zum Seiten-Div, gespeichert `/Rect` 150×50 pt.
 * - Ziehen verschiebt, Cmd/Strg+Z entfernt, Redo stellt wieder her, Entf löscht.
 * - `pdf.saveDocument()` → Seite hat `/Annots` mit `/Subtype /Stamp` und `/AP` (pdf-lib geprüft).
 * - Zweimal dasselbe Bild hintereinander: beide 160 pt (eigener Dateiname je Einsatz greift).
 * - Ansicht um 90° gedreht: Mitte sitzt am Klickpunkt, Bild steht aufrecht.
 * - Größen-API: `editor.width/height` (Seitenanteile) direkt nach `pasteEditor` setzen;
 *   `setDims()` ist nicht nötig, `#createCanvas` übernimmt die Werte.
 *
 * `viewer._layerProperties` ist nicht dokumentiert, aber in den Typen (`get _layerProperties():
 * any`) öffentlich. Fehlt der Weg (z. B. künftige PDF.js-Version), wirft die Funktion mit
 * einem Versionshinweis; ist das Dokument nur noch nicht geladen, mit „noch nicht bereit“ —
 * der Aufrufer zeigt die Meldung, statt still nichts zu tun.
 */

type Editor = {
    id: string;
    width: number | null;
    height: number | null;
};

type EditorLayer = {
    div: HTMLDivElement;
    pasteEditor(options: { mode: number }, params: Record<string, unknown>): Promise<void>;
};

type UiManager = {
    getLayer(i: number): unknown;
    updateMode(mode: number): Promise<void>;
    getEditors?(pageIndex: number): Iterable<Editor>;
    /** Rückgängig/Wiederholen des PDF.js-Befehlsstapels (Freitext, Zeichnen, Markieren, Stempel). */
    undo?(): void;
    redo?(): void;
    hasSomethingToUndo?(): boolean;
    hasSomethingToRedo?(): boolean;
};

let laufendeNummer = 0;

/**
 * Unterscheidet zwei Fälle, die für den Nutzer Verschiedenes bedeuten:
 * - `version`: PDF.js bietet den Weg nicht (mehr) an — `_layerProperties` fehlt oder der
 *   UI-Manager hat kein `getLayer/updateMode`. Warten hilft nicht.
 * - `laedt`: Der UI-Manager existiert erst, wenn das Dokument im Viewer steckt.
 */
function uiManagerPruefen(viewer: PDFViewer): { ui: UiManager } | { fehlt: "version" | "laedt" } {
    const props = (viewer as unknown as { _layerProperties?: { annotationEditorUIManager?: UiManager | null } })
        ._layerProperties;
    if (!props || typeof props !== "object" || !("annotationEditorUIManager" in props)) return { fehlt: "version" };
    const ui = props.annotationEditorUIManager;
    if (!ui) return { fehlt: "laedt" };
    if (typeof ui.getLayer !== "function" || typeof ui.updateMode !== "function") return { fehlt: "version" };
    return { ui };
}

export function uiManagerVon(viewer: PDFViewer): UiManager | null {
    const r = uiManagerPruefen(viewer);
    return "ui" in r ? r.ui : null;
}

/**
 * Klickpunkt relativ zur linken oberen Ecke des (gedreht angezeigten) Ebenen-Rechtecks →
 * Punkt in der ungedrehten Ebene. `breite/hoehe` = Bildschirmmaße des Rechtecks.
 */
export function bildschirmZuEbene(
    sx: number,
    sy: number,
    breite: number,
    hoehe: number,
    drehung: number,
): { x: number; y: number } {
    switch (((drehung % 360) + 360) % 360) {
        case 90:
            return { x: sy, y: breite - sx };
        case 180:
            return { x: breite - sx, y: hoehe - sy };
        case 270:
            return { x: hoehe - sy, y: sx };
        default:
            return { x: sx, y: sy };
    }
}

async function bildMasse(bild: Blob): Promise<{ w: number; h: number }> {
    const bitmap = await createImageBitmap(bild);
    const masse = { w: bitmap.width, h: bitmap.height };
    bitmap.close();
    return masse;
}

/**
 * Setzt ein Bild als Stempel auf Seite `seitenIndex` (0-basiert), Mittelpunkt an
 * (`cssX`, `cssY`) relativ zur linken oberen Ecke des Seiten-Divs.
 * `breitePt` = gewünschte Breite in PDF-Punkten (Unterschrift ≈ 150, Stempel ≈ 160).
 */
export async function stempelSetzen(
    viewer: PDFViewer,
    bild: Blob,
    ort: { seitenIndex: number; cssX: number; cssY: number },
    breitePt: number,
): Promise<void> {
    const geprueft = uiManagerPruefen(viewer);
    if ("fehlt" in geprueft) {
        throw new Error(
            geprueft.fehlt === "version"
                ? "Stempel lassen sich mit dieser PDF.js-Version nicht setzen."
                : "Der PDF-Editor ist noch nicht bereit.",
        );
    }
    const { ui } = geprueft;

    const masse = await bildMasse(bild);
    // Erst über den Viewer umschalten, damit dessen eigener Modus mitzieht — sonst hielte er
    // sich weiter für z. B. FREETEXT und ignorierte ein späteres „Text“ als „schon aktiv“.
    // Dann direkt beim UI-Manager warten: Der Viewer schaltet teils verzögert (nach dem
    // Neuzeichnen der Seiten), `updateMode` kehrt bei gleichem Modus sofort zurück.
    try {
        viewer.annotationEditorMode = { mode: AnnotationEditorType.STAMP };
    } catch {
        /* Viewer ohne Editor-Unterstützung — dann scheitert gleich `getLayer`. */
    }
    await ui.updateMode(AnnotationEditorType.STAMP);
    const layer = ui.getLayer(ort.seitenIndex) as EditorLayer | undefined;
    if (!layer) throw new Error("Die Seite ist noch nicht geladen.");

    // Seiten-Div hat einen Rahmen (`--page-border`); die Editor-Ebene beginnt innen.
    const pageDiv = (viewer.getPageView(ort.seitenIndex) as { div: HTMLElement }).div;
    const seite = pageDiv.getBoundingClientRect();
    const ebene = layer.div.getBoundingClientRect();
    const drehung = Number(layer.div.dataset.mainRotation ?? 0) || 0;
    const punkt = bildschirmZuEbene(
        ort.cssX - (ebene.left - seite.left),
        ort.cssY - (ebene.top - seite.top),
        ebene.width,
        ebene.height,
        drehung,
    );

    const vorher = new Set(Array.from(ui.getEditors?.(ort.seitenIndex) ?? [], (e) => e.id));
    laufendeNummer += 1;
    const datei = new File([bild], `baustein-${Date.now()}-${laufendeNummer}.png`, { type: bild.type || "image/png" });
    await layer.pasteEditor({ mode: AnnotationEditorType.STAMP }, { bitmapFile: datei, x: punkt.x, y: punkt.y });

    const neu = Array.from(ui.getEditors?.(ort.seitenIndex) ?? []).find((e) => !vorher.has(e.id));
    if (!neu) return; // Größe bleibt dann bei PDF.js' Voreinstellung (Bildpixel = pt).
    const { pageWidth, pageHeight } = (
        viewer.getPageView(ort.seitenIndex) as { viewport: { rawDims: { pageWidth: number; pageHeight: number } } }
    ).viewport.rawDims;
    const { width, height } = stempelMasse(breitePt, masse, pageWidth, pageHeight);
    neu.width = width;
    neu.height = height;
}
